import { useReactiveVar } from "@apollo/client";

import { membersWithoutRolePath } from "@apps/org-settings/components/competencies/helpers";
import useLabel from "@apps/use-label/use-label";
import { isAdminVar } from "@cache/cache";
import Callout from "@components/callout/callout";
import AppLink from "@components/link/link";

import { ProgramForm } from "../types";
import useProgramFormLoadPreview from "../use-program-form-load-preview";

/**
 * How many people in the review hold no role, so get none of these questions.
 * Its own component, as it asks the backend: only the review's own settings can
 * say who is in it.
 */
const WithoutRoleWarning = ({ form }: { form: ProgramForm }) => {
  const label = useLabel();
  const isAdmin = useReactiveVar(isAdminVar);
  const review = label("review");
  const { preview, waitingForASelection } = useProgramFormLoadPreview(form);
  const count = preview?.participantsWithoutRoleCount ?? 0;
  if (waitingForASelection || !preview || count === 0) {
    return null;
  }
  const everyone = count === preview.participantCount;
  return (
    <Callout theme="warning" icon="warning" size="small">
      <span>
        {everyone
          ? `Nobody in this ${review} has a role yet, so nobody gets these questions.`
          : `${count} of the ${preview.participantCount} people in this ${review} ${
              count === 1 ? "doesn't" : "don't"
            } have a role, so they won't get these questions.`}{" "}
        {/* The People table lists everyone without a role, not only this review's people. */}
        {isAdmin && (
          <AppLink
            to={membersWithoutRolePath(form.organizationId)}
            className="text-blue-600 hover:underline"
          >
            See everyone without a role
          </AppLink>
        )}
      </span>
    </Callout>
  );
};

/**
 * What Roles and Responsibilities questions are, at the top of their settings,
 * and how many people in the review have no role to draw them from. The settings
 * open only once the organization has career tracks, so there is always a
 * framework here.
 */
const RolesCoverageNote = ({ form }: { form: ProgramForm }) => {
  const label = useLabel();

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Each person is rated on the {label("competency", { pluralize: true })}{" "}
        and responsibilities of the role they hold on their career track. The
        questions are built for each person from their own role, so someone
        without a role gets none.
      </p>
      <WithoutRoleWarning form={form} />
    </div>
  );
};

export default RolesCoverageNote;
